import React, { FC, useContext, useEffect, useRef, useState } from "react";

import { css } from "@emotion/react";
import { ensurePercent } from "@intelligems/ig-utils";

import { DiscountProgressBarContext } from "../context";

interface BarProps {
  init: boolean;
}

export const Bar: FC<BarProps> = ({ init }) => {
  const [forceRefresh, setForceRefresh] = useState(false);
  const { theme, windowState, isMobile } = useContext(
    DiscountProgressBarContext
  );

  const containerRef = useRef<HTMLDivElement>(null);

  const outerProgressBarClass = css`
    width: 100%;
    height: ${theme.bar.height}px;
    background-color: ${theme.bar.inactiveColor};
    display: flex !important;
    justify-content: start;
    border-radius: ${theme.bar.height / 2}px;
    position: absolute;
  `;

  const sharedBarClass = css`
    height: ${theme.bar.height}px;
    background-color: ${theme.bar.activeColor} !important;
    border-radius: ${theme.bar.height / 2}px;
    transition: width ${init ? "0s" : "1s"};
  `;

  const getProgress = () => {
    if (windowState.discount.tiers.length === 1) {
      return `${
        windowState.progress.progress < 1
          ? windowState.progress.progress * 100
          : windowState.progress.progress
      }%`;
    }

    if (!containerRef.current?.clientWidth) {
      return `0%`;
    }

    if (windowState.progress.section >= windowState.discount.tiers.length) {
      return `100%`;
    }

    const getWidth = (
      totalSections: number,
      currentSectionProgress: number
    ) => {
      if (!containerRef.current) {
        return 0;
      }
      return (
        (1 / totalSections) *
        containerRef.current.clientWidth *
        currentSectionProgress
      );
    };

    let width = 0;
    let totalSections;

    if (windowState.discount.tiers.length === 2) {
      totalSections = 2;
    } else {
      totalSections = windowState.discount.tiers.length + 1;
    }

    for (let i = 0; i <= windowState.progress.section; i++) {
      const widthAddition = getWidth(
        totalSections,
        i < windowState.progress.section
          ? 1
          : ensurePercent(windowState.progress.progress)
      );

      width += widthAddition;
    }

    return `${width}px`;
  };

  const progress = getProgress();
  const innerProgressBarClass = css`
    ${sharedBarClass};
    position: absolute;
    display: block !important;
    width: ${progress};
  `;

  useEffect(() => {
    const timeout = setTimeout(() => setForceRefresh(true), 100);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <div
      ref={containerRef}
      css={outerProgressBarClass}
      className={"ig-dp-inactive-bar"}
    >
      <div css={innerProgressBarClass}></div>
    </div>
  );
};
